/**
 * Solo, played entirely on this phone.
 *
 * The code is dealt here, every guess is scored here, and the game is kept in
 * local storage, so a solo game plays the same with or without a connection.
 * The rules mirror internal/game: ValidateGuess, GenerateCombo, ForfeitCodes
 * and the mode alphabets. tests/unit/solo.spec.js holds them to the same cases
 * the Go tests use.
 *
 * The only thing that leaves the phone is a count of how much solo was played,
 * per mode, with nothing in it that names the player or a game.
 */

import { COLORS, CODE_LENGTH } from './pegs.js';
import { persist, readJSON } from './storage.js';

/** Where the one solo game is kept. There is one at a time. */
export const SOLO_KEY = 'unkoad.solo';

/** Where the play not yet reported is added up. */
export const TALLY_KEY = 'unkoad.soloTally';

/** The player on a solo board. Not an identity: nothing else is on the board. */
export const ME = 'me';

/** The URLs the solo board posts to. Answered by soloFetch, never the network. */
export const SOLO_GUESS = '/solo/local/guess';
export const SOLO_FORFEIT = '/solo/local/forfeit';

/** The mode labels, mirroring Mode.Label. */
const LABELS = { Four: 'Fab Four', Seven: 'Lucky Seven', Ten: 'Perfect Ten', Seventy: 'Savage Seventy' };

const TALLY_FIELDS = ['games', 'finished', 'won', 'forfeits', 'guesses'];

/**
 * Reads a persisted mode name, mirroring ParseMode but refusing rather than
 * falling back: a stored game in a mode this build cannot play is not a game.
 */
export function parseMode(name) {
    if (name === 'Four' || name === 'Seven' || name === 'Seventy') return { kind: name, color: '' };
    if (typeof name === 'string' && name.startsWith('Ten|')) {
        const color = name.slice(4);
        if (COLORS.some((c) => c.code === color && c.tenMode)) return { kind: 'Ten', color };
    }
    return null;
}

/** The mode's alphabet in display order, mirroring Mode.Codes. */
export function modeCodes(name) {
    const mode = parseMode(name);
    if (!mode) return [];
    switch (mode.kind) {
    case 'Four':
        return COLORS.slice(0, 4).map((c) => c.code + c.code);
    case 'Seven':
        return COLORS.map((c) => c.code + c.code);
    case 'Ten':
        return digits(mode.color);
    default:
        return COLORS.flatMap((c) => digits(c.code));
    }
}

function digits(color) {
    return Array.from({ length: 10 }, (_, d) => `${color}${d}`);
}

/** The mode's display name. */
export function modeLabel(name) {
    const mode = parseMode(name);
    return mode ? LABELS[mode.kind] : '';
}

/** Where a mode's solo board is served, mirroring soloPath. */
export function soloPath(name) {
    return `/solo/${name.replace('|', '-')}`;
}

/** The mode a solo board's path names, or '' for a path that names none. */
export function modeFromPath(path) {
    const slug = String(path || '').replace(/^\/solo\//, '').replace('-', '|');
    return parseMode(slug) ? slug : '';
}

/**
 * A uniform integer below n, from the platform's cryptographic source.
 *
 * Rejection rather than a modulo, which would favour the low values. The code
 * is only as secret as the phone, but it should not be predictable either.
 */
export function randomBelow(n, crypto = globalThis.crypto) {
    const limit = Math.floor(0x100000000 / n) * n;
    const word = new Uint32Array(1);
    for (;;) {
        crypto.getRandomValues(word);
        if (word[0] < limit) return word[0] % n;
    }
}

/** Turns a picker value into a mode name, drawing Ten's colour as the server did. */
export function pickMode(value, random = randomBelow) {
    if (value === 'Ten') {
        const eligible = COLORS.filter((c) => c.tenMode);
        return `Ten|${eligible[random(eligible.length)].code}`;
    }
    return parseMode(value) ? value : 'Four';
}

/** Draws a secret code for the mode, mirroring GenerateCombo. */
export function dealCombo(name, random = randomBelow) {
    const codes = modeCodes(name);
    return Array.from({ length: CODE_LENGTH }, () => codes[random(codes.length)]);
}

/**
 * Scores a guess, mirroring ValidateGuess: exact hits first, each consuming the
 * code peg it matched, then right-peg-wrong-place against what is left.
 */
export function scoreGuess(combo, guess) {
    let placement = 0;
    let total = 0;
    const remaining = [];
    const exact = combo.map((code, i) => code === guess[i]);

    combo.forEach((code, i) => {
        if (exact[i]) {
            placement++;
            total++;
        } else {
            remaining.push(code);
        }
    });

    guess.forEach((code, i) => {
        if (exact[i]) return;
        const k = remaining.indexOf(code);
        if (k >= 0) {
            total++;
            remaining.splice(k, 1);
        }
    });

    return { placement, total };
}

/**
 * The fixed row a give-up plays, mirroring ForfeitCodes: "lost" in a colour the
 * code cannot hold, so it can never score as a win.
 */
export function forfeitCodes(name, combo) {
    const mode = parseMode(name);
    if (mode && mode.kind === 'Ten') {
        return mode.color === 'w' ? ['y1', 'y0', 'y5', 'y7', 'yy'] : ['w1', 'w0', 'w5', 'w7', 'ww'];
    }
    if (combo.length > 4 && combo[4] === 'ww') return ['w1', 'w0', 'w5', 'w7', 'yy'];
    return ['w1', 'w0', 'w5', 'w7', 'ww'];
}

/** The solo game kept on this phone, or null. */
export function loadGame(storage) {
    const game = readJSON(storage, SOLO_KEY);
    if (!game || typeof game !== 'object') return null;
    if (!parseMode(game.mode) || !Array.isArray(game.combo) || !Array.isArray(game.rows)) return null;
    return game;
}

/** The kept game when it is still being played, or null. */
export function ongoingGame(storage) {
    const game = loadGame(storage);
    return game && !game.gameOver ? game : null;
}

function saveGame(storage, game) {
    persist(storage, SOLO_KEY, JSON.stringify(game));
}

/** Deals a new game in the mode, replacing whatever was kept. */
export function startGame(storage, name, random = randomBelow) {
    const game = {
        id: Array.from({ length: 4 }, () => randomBelow(0x10000).toString(16)).join(''),
        mode: name,
        combo: dealCombo(name, random),
        rows: [],
        gameOver: false,
        won: false,
    };
    saveGame(storage, game);
    return game;
}

/**
 * The game a mode's board opens on: the kept one when it is in that mode, a new
 * one otherwise. A finished game in the mode is shown rather than replaced, so
 * reloading a board that just ended does not deal a new code under it.
 */
export function openGame(storage, name, random = randomBelow) {
    const kept = loadGame(storage);
    if (kept && kept.mode === name) return kept;
    return startGame(storage, name, random);
}

/** The board's view of a game: what createBoard and ingest read. */
export function boardState(game) {
    return {
        rows: game.rows,
        turn: ME,
        game_over: game.gameOver,
        won: game.won,
        chat: {},
        awaiting_opponent: false,
        opponent_name: '',
    };
}

/** Plays a guess or the give-up row onto the kept game, or says why not. */
export function play(storage, codes, forfeit = false) {
    const game = loadGame(storage);
    if (!game) return { error: 'There is no solo game here any more.', status: 404 };
    if (game.gameOver) return { error: 'This game is already over.', status: 409 };

    const alphabet = modeCodes(game.mode);
    const valid = Array.isArray(codes) && codes.length === CODE_LENGTH && codes.every((c) => alphabet.includes(c));
    if (!forfeit && !valid) return { error: 'That guess is not valid for this mode.', status: 400 };

    const played = forfeit ? forfeitCodes(game.mode, game.combo) : [...codes];
    const result = scoreGuess(game.combo, played);
    game.rows.push({ line_number: game.rows.length + 1, codes: played, result, user: ME, forfeit });

    const tally = {};
    if (game.rows.length === 1) tally.games = 1;
    if (!forfeit) tally.guesses = 1;
    if (forfeit) {
        game.gameOver = true;
        tally.forfeits = 1;
    } else if (result.placement === CODE_LENGTH) {
        game.gameOver = true;
        game.won = true;
        tally.won = 1;
    }
    if (game.gameOver) tally.finished = 1;

    saveGame(storage, game);
    addTally(storage, game.mode, tally);
    return { game };
}

/**
 * The board's fetch on a solo board. It answers the two URLs the board posts
 * to in the shape the server's guess response has, so the board cannot tell
 * the difference.
 */
export function createSoloFetch(storage) {
    return async function soloFetch(url, options = {}) {
        let outcome;
        if (url === SOLO_GUESS) {
            let codes = null;
            try {
                codes = JSON.parse(options.body || '{}').codes;
            } catch {
                codes = null;
            }
            outcome = play(storage, codes);
        } else if (url === SOLO_FORFEIT) {
            outcome = play(storage, null, true);
        } else {
            outcome = { error: 'Not available on a solo board.', status: 404 };
        }

        const body = outcome.error ? { error: outcome.error } : boardState(outcome.game);
        return {
            ok: !outcome.error,
            status: outcome.error ? outcome.status : 200,
            json: async () => body,
        };
    };
}

/** The unreported play, per mode. */
export function readTally(storage) {
    const stored = readJSON(storage, TALLY_KEY);
    const out = {};
    if (!stored || typeof stored !== 'object') return out;
    for (const [mode, counts] of Object.entries(stored)) {
        if (!parseMode(mode) || !counts || typeof counts !== 'object') continue;
        const clean = {};
        for (const f of TALLY_FIELDS) clean[f] = Math.max(0, Math.floor(Number(counts[f]) || 0));
        if (TALLY_FIELDS.some((f) => clean[f] > 0)) out[mode] = clean;
    }
    return out;
}

function writeTally(storage, tally) {
    persist(storage, TALLY_KEY, JSON.stringify(tally));
}

function addTally(storage, mode, counts) {
    const tally = readTally(storage);
    const row = tally[mode] || Object.fromEntries(TALLY_FIELDS.map((f) => [f, 0]));
    for (const [f, n] of Object.entries(counts)) row[f] += n;
    tally[mode] = row;
    writeTally(storage, tally);
}

/**
 * Sends the unreported play, and takes off what the server accepted.
 *
 * What was sent is subtracted rather than the tally cleared, so a game finished
 * while the report was in flight is still counted next time. A report that
 * fails leaves everything in place for the next try.
 */
export async function reportTally(storage, send = globalThis.fetch) {
    const sent = readTally(storage);
    if (!Object.keys(sent).length || typeof send !== 'function') return false;

    try {
        const res = await send('/solo/report', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ modes: sent }),
        });
        if (!res.ok) return false;
    } catch {
        return false;
    }

    const now = readTally(storage);
    for (const [mode, counts] of Object.entries(sent)) {
        if (!now[mode]) continue;
        for (const f of TALLY_FIELDS) now[mode][f] = Math.max(0, now[mode][f] - counts[f]);
    }
    writeTally(storage, readTallyFrom(now));
    return true;
}

function readTallyFrom(tally) {
    const out = {};
    for (const [mode, counts] of Object.entries(tally)) {
        if (TALLY_FIELDS.some((f) => counts[f] > 0)) out[mode] = counts;
    }
    return out;
}
